import { EApplicantType, EMerchantNotificationCode, EOriginSystemId } from '@if/core/types'
import {
  CircleNotifications as CircleNotificationsIcon,
  Message as MessageIcon,
} from '@mui/icons-material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Badge, IconButton, List, ListItem } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  merchantNotificationCodes: EMerchantNotificationCode[]
  requiredDocuments: RequiredDocument[]
  originSystemId: EOriginSystemId
  hasUnreadMessages?: boolean
}

const FR = 'fr'

const MerchantNotificationIcons = ({ merchantNotificationCodes, requiredDocuments, originSystemId, hasUnreadMessages }: Props) => {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const getFullDocumentName = useCallback(
    (documentRequired: RequiredDocument) => {
      let applicantSuffix = ''
      if (documentRequired.applicantType === EApplicantType.Applicant) {
        applicantSuffix = ` - ${t('common.applicant')}`
      } else if (documentRequired.applicantType === EApplicantType.CoApplicant) {
        applicantSuffix = ` - ${t('common.coApplicant')}`
      }

      return i18n.resolvedLanguage === FR
        ? `${documentRequired.nameFr}${applicantSuffix}`
        : `${documentRequired.nameEn}${applicantSuffix}`
    },
    [i18n.resolvedLanguage, t],
  )

  return (
    <div>
      {hasUnreadMessages && (
        <Tooltip title={t('common.unreadMessages')}>
          <IconButton size="small">
            <Badge variant="dot" color="error">
              <MessageIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      {merchantNotificationCodes.length > 0 && (
        <Tooltip
          title={merchantNotificationCodes.map((notification, index) => (
            <div key={notification}>
              {t(`notificationsTitles.${notification}`)}
              {index !== merchantNotificationCodes.length - 1 && (
                <>
                  <br />
                  <br />
                </>
              )}
            </div>
          ))}
        >
          <IconButton size="small">
            <CircleNotificationsIcon />
          </IconButton>
        </Tooltip>
      )}
      {requiredDocuments.length > 0 && originSystemId === EOriginSystemId.MerchantDashboard && (
        <Tooltip
          title={
            <List>
              {t(`common.requiredDocuments`)}
              {requiredDocuments.map((doc) => {
                return <ListItem key={doc.typeId}>{getFullDocumentName(doc)}</ListItem>
              })}
            </List>
          }
        >
          <IconButton size="small">
            <UploadFileRoundedIcon />
          </IconButton>
        </Tooltip>
      )}
      {originSystemId !== EOriginSystemId.MerchantDashboard && (
        <Tooltip
          title={
            originSystemId === EOriginSystemId.Backoffice
              ? t('viewCreditApplication.limitedAccessBackOffice')
              : t('viewCreditApplication.limitedAccessSubscription')
          }
        >
          <IconButton size="small">
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default MerchantNotificationIcons
